import {AfterViewInit, Component, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../../shared/api-request.service";
import {UtilsService} from "../../shared/utils.service";
import {tap} from "rxjs/operators";
import {ApiDataSource} from "../../utils/api-data-source";
import {AssetModel} from "../../models/asset.model";
import {
  SafetyObservationsViewComponent
} from "../../safety-observations/safety-observations-view/safety-observations-view.component";
import {UserPublicProfileComponent} from "../../shared/user-public-profile/user-public-profile.component";
import {RBACService} from "../../shared/rbac.service";
import {
  SafetyObservationsSelectorComponent
} from "../../shared/safety-observations-selector/safety-observations-selector.component";
import {
  SafetyObservationsSelectorFilterComponent
} from "../../shared/safety-observations-selector-filter/safety-observations-selector-filter.component";

@Component({
  selector: 'app-assets-view-safety-observations',
  templateUrl: './assets-view-safety-observations.component.html',
  styleUrls: ['./assets-view-safety-observations.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetsViewSafetyObservationsComponent implements OnInit, AfterViewInit {

  @Input('asset_id') asset_id: number;
  @Input('asset') asset: AssetModel;

  dataSource: AssetsViewSafetyObservationsDataSource = new AssetsViewSafetyObservationsDataSource(this.app, this.api);

  // Built dynamically in ngOnInit(). The checkbox column only drives Unlink Selected, so it is
  // added for a user who holds the asset edit permission and omitted otherwise.
  listColumns: string[] = [];

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // RBAC permission flag for the link and unlink actions. Managing an asset's related records is
  // part of the asset workflow, so it is governed by the asset edit permission rather than by the
  // permissions of the related feature. Resolved once in ngOnInit().
  canEdit: boolean = false;

  // Opening the safety observation record itself belongs to the Safety Observations feature, so it
  // carries that feature's view permission instead. Resolved once in ngOnInit().
  canViewSafetyObservations: boolean = false;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) { }

  ngOnInit() {
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, RBACService.RBAC_A.A_EDIT)
    ], false);

    // The view dialog loads the observation from the Safety Observations API, which gates on that
    // feature. Linking and unlinking stay on the asset edit permission above, because those write the
    // asset's own pivot.
    this.canViewSafetyObservations = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_SAFETY_OBSERVATIONS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_SAFETY_OBSERVATIONS, RBACService.RBAC_A.A_VIEW)
    ], false);

    this.listColumns = [];
    if (this.canEdit) {
      this.listColumns.push('select');
    }
    this.listColumns.push(
      'id',
      'so_description',
      'so_category',
      'so_type',
      'so_priority',
      'so_status',
      'so_rating',
      'site',
      'reported_by_user',
      'date_created',
      'actions'
    );

    this.dataSource.asset_id = this.asset_id;
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter) => {
      this.dataSource.order_by = sorter['active'];
      this.dataSource.order = sorter['direction'];

      this.dataSource.getData();
    })).subscribe();
  }

  /**
   * Open the filter dialog and apply the selected filters.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(
      SafetyObservationsSelectorFilterComponent,
      {
        so_category: this.dataSource.so_category,
        so_type: this.dataSource.so_type,
        so_priority: this.dataSource.so_priority,
        so_status: this.dataSource.so_status,
        so_rating: this.dataSource.so_rating,
        site_ids: this.dataSource.site_ids,
        contractor_ids: this.dataSource.contractor_ids,
        user_ids: this.dataSource.user_ids,
        asset_ids: this.dataSource.asset_ids,
        reported_by_ids: this.dataSource.reported_by_ids,
        date_filter_range: this.dataSource.date_filter_range,
        hide_assets: this.dataSource.hide_assets
      },
      {
        width: '900px'
      },
      (results) => {
        if (typeof results === 'undefined') { return; }

        this.dataSource.so_category = results.so_category ?? '';
        this.dataSource.so_type = results.so_type ?? '';
        this.dataSource.so_priority = results.so_priority ?? '';
        this.dataSource.so_status = results.so_status ?? '';
        this.dataSource.so_rating = results.so_rating ?? '';
        this.dataSource.site_ids = results.site_ids ?? [];
        this.dataSource.contractor_ids = results.contractor_ids ?? [];
        this.dataSource.user_ids = results.user_ids ?? [];
        this.dataSource.asset_ids = results.asset_ids ?? [];
        this.dataSource.reported_by_ids = results.reported_by_ids ?? [];
        this.dataSource.date_filter_range = results.date_filter_range ?? [];

        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Display a dialog to link/unlink safety observations to the asset.
   *
   * @return {void}
   */
  onManage(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to manage the safety observations linked to this asset.');
      return;
    }

    this.utils.showComponentDialog(
      SafetyObservationsSelectorComponent,
      {
        selected: this.dataSource.allIds,
        multiple: true,
        hide_assets: this.dataSource.hide_assets
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results != 'undefined') {
          // Set the newly selected ids.
          this.dataSource.allIds = results;

          // Send API request to sync safety observations.
          this.api.makeRequest('put', `v2/assets/${this.asset_id}/safety-observations`, {
            ids: this.dataSource.allIds
          })
            .then(() => {
              // Refresh the list.
              this.dataSource.getData();
            })
            .catch((errorResponse) => {
              this.utils.handleAPIErrors(errorResponse);
            });
        }
      }
    );
  }

  /**
   * Unlinks the selected safety observation.
   *
   * @param {number} id - The ID of the safety observation to be unlinked.
   * @return {void}
   */
  onUnlink(id: number): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to unlink safety observations from this asset.');
      return;
    }

    this.utils.showModal(
      'Unlink Selected Safety Observation',
      'Are you sure you want to unlink the selected Safety Observation?',
      () => {
        this.unlink(id, 'Safety Observation was unlinked');
      });
  }

  /**
   * Unlinks the selected safety observations.
   *
   * @return {void}
   */
  onUnlinkSelected(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to unlink safety observations from this asset.');
      return;
    }

    this.utils.showModal(
      'Unlink Selected Safety Observations',
      'Are you sure you want to unlink the selected Safety Observations?',
      () => {
        this.unlink(this.dataSource.selection.selected.join(','), 'Safety Observations were unlinked');
      });
  }

  /**
   * Unlinks safety observations with the specified IDs.
   *
   * @param {any} ids - The safety observation IDs to be unlinked.
   * @param msg
   * @return {void}
   */
  private unlink(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/assets/${this.asset_id}/safety-observations`, {},
      {
        safety_observation_ids: ids
      })
      .then(() => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * View the associated safety observation.
   *
   * @param {number} safety_observation_id - The ID of the safety observation to view.
   * @return {void}
   */
  onView(safety_observation_id: number): void {
    if (!this.canViewSafetyObservations) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view safety observations.');
      return;
    }

    this.utils.showComponentDialog(
      SafetyObservationsViewComponent,
      {
        safety_observation_id: safety_observation_id
      },
      {
        width: '90%'
      },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData();
      }
    );
  }

  /**
   * Open the user's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
      }
    );
  }
}

export class AssetsViewSafetyObservationsDataSource extends ApiDataSource {
  order_by = 'date_created';
  order = 'desc';
  asset_id: number;

  // Filter options (mirrors the main Safety Observations list).
  so_category: string = '';
  so_type: string = '';
  so_priority: string = '';
  so_status: string = '';
  so_rating: string = '';
  site_ids: number[] = [];
  contractor_ids: number[] = [];
  user_ids: number[] = [];
  asset_ids: number[] = [];
  reported_by_ids: number[] = [];
  date_filter_range: Date[] = [];
  hide_assets: boolean = true;

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/assets/${this.asset_id}/safety-observations`, resetOffset, {
      so_category: this.so_category || '',
      so_type: this.so_type || '',
      so_priority: this.so_priority || '',
      so_status: this.so_status || '',
      so_rating: this.so_rating || '',
      site_ids: this.site_ids.length ? this.site_ids.join(',') : '',
      contractor_ids: this.contractor_ids.length ? this.contractor_ids.join(',') : '',
      user_ids: this.user_ids.length ? this.user_ids.join(',') : '',
      asset_ids: this.asset_ids.length ? this.asset_ids.join(',') : '',
      reported_by_ids: this.reported_by_ids.length ? this.reported_by_ids.join(',') : '',
      date_filter_range: this.date_filter_range && this.date_filter_range.length > 1 ? [
        this.date_filter_range[0].getTime() / 1000,
        this.date_filter_range[1].getTime() / 1000
      ].join(',') : ''
    });
  }
}
